import { del, get, post } from './client'
import type {
  AuthTokens,
  LoginCredentials,
  OAuthRedirectResponse,
  PasskeySummary,
  PasswordResetData,
  RegisterData,
  User,
} from '@/types'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser'

// WebAuthn ceremony envelopes returned by the backend (flow id + options blob).
interface RegistrationOptionsResponse {
  flowId: string
  options: PublicKeyCredentialCreationOptionsJSON
}

interface AuthenticationOptionsResponse {
  flowId: string
  options: PublicKeyCredentialRequestOptionsJSON
}

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

/**
 * Authenticate with email + password.
 * The JWT login endpoint expects `username` (which is the user's email) and `password`.
 */
export async function login(credentials: LoginCredentials): Promise<AuthTokens> {
  return post<AuthTokens>('/app/auth/login', credentials)
}

/**
 * Register a new user account.
 * Returns the newly created User resource.
 */
export async function register(data: RegisterData): Promise<User> {
  return post<User>('/app/users/register', data)
}

/**
 * Exchange a refresh token for a new access token pair.
 */
export async function refreshToken(refreshToken: string): Promise<AuthTokens> {
  return post<AuthTokens>('/app/auth/refresh', { refreshToken })
}

/**
 * Verify a user's email address using the verification token sent by email.
 */
export async function verifyEmail(token: string): Promise<void> {
  await get<void>(`/app/verification/verify?token=${encodeURIComponent(token)}`)
}

/**
 * Re-send the email verification link for the given email address.
 */
export async function regenerateVerification(email: string): Promise<void> {
  await post<void>('/app/verification/regenerate', { email })
}

/**
 * Request a password-reset email for the given address.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await post<void>('/app/auth/password/reset', { email })
}

/**
 * Set a new password using the token received in the password-reset email.
 */
export async function resetPassword(data: PasswordResetData): Promise<void> {
  // Backend route carries the token in the path; body holds the new password.
  await post<void>(
    `/app/auth/password/reset/${encodeURIComponent(data.token)}`,
    { plainPassword: data.plainPassword },
  )
}

/**
 * Retrieve the Google OAuth2 authorization URL to redirect the user to.
 */
export async function getGoogleAuthUrl(): Promise<OAuthRedirectResponse> {
  return get<OAuthRedirectResponse>('/app/connect/google')
}

/**
 * Exchange the authorization code returned by Google for application tokens.
 */
export async function exchangeOAuthCode(code: string): Promise<AuthTokens> {
  return post<AuthTokens>('/app/oauth/exchange-code', { code })
}

// ---------------------------------------------------------------------------
// Passkeys (WebAuthn)
// ---------------------------------------------------------------------------

/** Begin passkey registration — returns creation options for the current user. */
export async function getPasskeyRegistrationOptions(): Promise<RegistrationOptionsResponse> {
  return post<RegistrationOptionsResponse>('/app/passkeys/registration/options', {})
}

/** Finish passkey registration by submitting the browser's attestation. */
export async function verifyPasskeyRegistration(
  flowId: string,
  credential: RegistrationResponseJSON,
  label?: string,
): Promise<PasskeySummary> {
  return post<PasskeySummary>('/app/passkeys/registration', { flowId, credential, label })
}

/** Begin a passwordless passkey login — returns request options (public). */
export async function getPasskeyAuthenticationOptions(): Promise<AuthenticationOptionsResponse> {
  return post<AuthenticationOptionsResponse>('/app/passkeys/authentication/options', {})
}

/** Finish a passwordless passkey login — exchanges the assertion for tokens. */
export async function verifyPasskeyAuthentication(
  flowId: string,
  credential: AuthenticationResponseJSON,
): Promise<AuthTokens> {
  return post<AuthTokens>('/app/passkeys/authentication', { flowId, credential })
}

/** List the current user's registered passkeys. */
export async function listPasskeys(): Promise<PasskeySummary[]> {
  const data = await get<{ items: PasskeySummary[] }>('/app/passkeys')
  return data.items
}

/** Remove a passkey by id. */
export async function deletePasskey(id: number): Promise<void> {
  await del(`/app/passkeys/${id}`)
}
